import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import Slider, { Settings } from 'react-slick'
import { landingAssetsForLocale } from '../config/landingAssets'
import { CuratedPhotoForPhotoProducts } from '../types'

const SliderComponent = Slider as unknown as React.ComponentClass<Settings>

function MemoriesCarousel({
  slides,
  isUsingFallback,
}: {
  slides: Array<[string, string]>
  isUsingFallback: boolean
}) {
  const { i18n } = useTranslation('landingPage')
  const assets = landingAssetsForLocale(i18n.language)
  const carouselSettings: Settings = useMemo(
    () => ({
      dots: true,
      infinite: true,
      slidesToShow: 1,
      slidesToScroll: 1,
      autoplay: true,
      speed: 1200,
      autoplaySpeed: 3000,
      cssEase: 'ease',
      arrows: false,
      swipeToSlide: true,
      appendDots: (dots: JSX.Element) => (
        <div className='absolute bottom-4 left-0 right-0 z-10'>
          <ul className='m-0 flex list-none justify-center gap-2 p-0'>{dots}</ul>
        </div>
      ),
      customPaging: () => (
        <div className='h-2 w-2 rounded-full bg-gray-300 transition-all duration-200' />
      ),
    }),
    [],
  )

  const containerClasses = 'relative rounded-lg overflow-hidden h-[218px] sm:h-[260px]'
  const sliderClassName = 'memories-carousel'

  // Fallback to single static image if using fallback or no slides
  if (isUsingFallback || slides.length === 0) {
    return (
      <div className={containerClasses}>
        <img
          src={assets.memoryImg1}
          alt=''
          className='h-full w-full object-cover'
          loading='eager'
        />
      </div>
    )
  }

  return (
    <div className={containerClasses}>
      <style>{`
        .${sliderClassName} {
          position: relative;
          height: 100%;
        }
        .${sliderClassName} .slick-list,
        .${sliderClassName} .slick-track,
        .${sliderClassName} .slick-slide,
        .${sliderClassName} .slick-slide > div {
          height: 100%;
        }
        .${sliderClassName} .slick-dots {
          position: absolute !important;
          bottom: 16px !important;
          left: 0 !important;
          right: 0 !important;
          z-index: 10 !important;
        }
      `}</style>
      <SliderComponent className={sliderClassName} {...carouselSettings}>
        {slides.map((slide, slideIdx) => (
          <div key={slideIdx} className='h-full px-0.5'>
            <div className='grid h-full grid-cols-2 gap-0.5'>
              <img
                src={slide[0]}
                alt=''
                className='h-full w-full rounded-bl-lg rounded-tl-lg object-cover'
                loading={slideIdx === 0 ? 'eager' : 'lazy'}
              />
              <img
                src={slide[1]}
                alt=''
                className='h-full w-full rounded-br-lg rounded-tr-lg object-cover'
                loading={slideIdx === 0 ? 'eager' : 'lazy'}
              />
            </div>
          </div>
        ))}
      </SliderComponent>
    </div>
  )
}

export default function MemoriesSection({
  curatedPhotos,
  isUsingFallback,
}: {
  curatedPhotos: CuratedPhotoForPhotoProducts[]
  isUsingFallback: boolean
}) {
  const { t } = useTranslation('landingPage')

  // Create slides with 2 photos per slide
  const slides = useMemo<Array<[string, string]>>(() => {
    if (isUsingFallback || curatedPhotos.length < 6) {
      return []
    }
    const photoUrls = curatedPhotos.slice(0, 6).map(photo => photo.croppedUrl)
    return [
      [photoUrls[0], photoUrls[1]],
      [photoUrls[2], photoUrls[3]],
      [photoUrls[4], photoUrls[5]],
    ]
  }, [curatedPhotos, isUsingFallback])

  return (
    <section className='bg-[#FEFBE9] px-4 py-10'>
      <div className='space-y-6'>
        <div className='text-center'>
          <h2 className='text-gray-12 tracking-0 whitespace-pre-line text-2xl font-semibold leading-[140%]'>
            {t('memories.title')}
          </h2>
        </div>
        <MemoriesCarousel slides={slides} isUsingFallback={isUsingFallback} />
        <p className='localized-body-copy text-gray-12 tracking-0 whitespace-pre-line text-base font-normal leading-[160%]'>
          {t('memories.body')}
        </p>
      </div>
    </section>
  )
}
